import { useMemo } from "react";
import { Heading } from "@userFrontend/components/Heading";
import { ChoiceChip } from "@root/shared/components/ChoiceChip";
import { AccordionToggle } from "@root/shared/components/AccordionToggle";
import { TextField } from "@root/shared/components/TextField";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextButton } from "@root/shared/components/TextButton";

import { AreaSelector } from "./uiParts/AreaSelector";
import { SearchActions } from "./uiParts/SearchActions";
import { type BusinessTypeOptions, BusinessTypeSelector } from "./uiParts/BusinessTypeSelector";
import { useBusinessTypeFiltering } from "./useBusinessTypeFiltering";
import * as styles from "./styles.css";
import type {
  BusinessTypeFilterOption,
  Choice,
  ChoiceGroup,
  FormValues,
  OptionProps,
} from "./types";
import { SALARY_TYPE_PERCENTAGE } from "./useSalaryData";
import {
  type SearchConditionsConfig,
  SearchConditionsModal,
} from "./uiParts/SearchConditionsModal";

type Props = {
  registrationStep1Url: string;
  loginUrl: string;
  isLoggedIn: boolean;
  isSp: boolean;
  isBiyo: boolean;
  currentWorksCount: number;
  isAnimating: boolean;
  formValues: FormValues;
  onFormValuesChange: (values: Partial<FormValues>) => void;
  cityChoices: ChoiceGroup[];
  lineChoices: ChoiceGroup[];
  popularAreaChoices: Choice[];
  prefectureOptions: OptionProps[];
  businessTypeOptions: BusinessTypeOptions[];
  employmentTypeOptions: BusinessTypeFilterOption[];
  salaryTypeOptions: OptionProps[];
  salaryMinOptions: OptionProps[];
  salaryMaxOptions: OptionProps[];
  isSalaryDataLoading: boolean;
  workingDayOptions: OptionProps[];
  holidayOptions: OptionProps[];
  workstyleOptions: OptionProps[];
  preferentialConditionsOptions: BusinessTypeFilterOption[];
  occupationOptions: BusinessTypeFilterOption[];
  facilityTypeOptions: BusinessTypeFilterOption[];
  customerTypeOptions: BusinessTypeFilterOption[];
  featureOptions: BusinessTypeFilterOption[];
  experienceOptions: BusinessTypeFilterOption[];
  welfareOptions: OptionProps[];
  workDescriptionOptions: BusinessTypeFilterOption[];
  userCredentialOptions: BusinessTypeFilterOption[];
  isActionButtonDisabled: boolean;
  onSearch: () => void;
  onSave: () => void;
  onClear: () => void;
  savedConditions: SearchConditionsConfig;
  searchedConditions: SearchConditionsConfig;
  appDlUrl: string;
  appImgSrc: string;
  appGaLabel: string;
};

export const Presentation = (props: Props) => {
  const {
    registrationStep1Url,
    loginUrl,
    isLoggedIn,
    isSp,
    isBiyo,
    currentWorksCount,
    isAnimating,
    formValues,
    onFormValuesChange,
    cityChoices,
    lineChoices,
    popularAreaChoices,
    prefectureOptions,
    businessTypeOptions,
    employmentTypeOptions,
    salaryTypeOptions,
    salaryMinOptions,
    salaryMaxOptions,
    isSalaryDataLoading,
    workingDayOptions,
    holidayOptions,
    workstyleOptions,
    preferentialConditionsOptions,
    occupationOptions,
    facilityTypeOptions,
    customerTypeOptions,
    featureOptions,
    experienceOptions,
    welfareOptions,
    workDescriptionOptions,
    userCredentialOptions,
    isActionButtonDisabled,
    onSearch,
    onSave,
    onClear,
    savedConditions,
    searchedConditions,
    appDlUrl,
    appImgSrc,
    appGaLabel,
  } = props;

  const {
    filteredPreferentialConditionsOptions,
    filteredOccupationOptions,
    filteredFacilityTypeOptions,
    filteredCustomerTypeOptions,
    filteredFeatureOptions,
    filteredExperienceOptions,
    filteredWorkDescriptionOptions,
    filteredUserCredentialOptions,
    filteredEmploymentTypeOptions,
    handleBusinessTypesChange,
  } = useBusinessTypeFiltering({
    businessTypeOptions,
    preferentialConditionsOptions,
    occupationOptions,
    facilityTypeOptions,
    customerTypeOptions,
    featureOptions,
    experienceOptions,
    workDescriptionOptions,
    userCredentialOptions,
    employmentTypeOptions,
    formValues,
    onBusinessTypesChange: onFormValuesChange,
  });

  // preferentialConditionsOptionsで選択できるのは、各種条件（feature等）の選択肢に含まれるもののみ。
  const preferentialConditionsAllSet = new Set(
    filteredPreferentialConditionsOptions.map((o) => o.value),
  );

  const filterPreferentialValues = (values: string[]) =>
    values.filter((value) => preferentialConditionsAllSet.has(value));

  const selectedPreferentialConditions = [
    ...filterPreferentialValues(formValues["q[feature][]"]),
    ...filterPreferentialValues(formValues["q[welfare][]"]),
    ...filterPreferentialValues(formValues["q[working_day][]"]),
    ...filterPreferentialValues(formValues["q[holiday][]"]),
    ...filterPreferentialValues(formValues["q[workstyle][]"]),
    ...filterPreferentialValues(formValues["q[occupation][]"]),
    ...filterPreferentialValues(formValues["q[facility_type][]"]),
    ...filterPreferentialValues(formValues["q[work_description][]"]),
    ...filterPreferentialValues(formValues["q[user_credential][]"]),
    ...filterPreferentialValues(formValues["q[experience][]"]),
    ...filterPreferentialValues(formValues["q[customer_type][]"]),
    ...(formValues["q[salon_tour_flag]"] ? ["salon_tour_flag"] : []),
    ...(formValues["q[client_end_up_close_flag]"] ? ["client_end_up_close_flag"] : []),
  ];

  const handlePreferentialConditionsChange = (preferentialConditions: string[]) => {
    const preserveNonPreferential = (currentValues: string[]) =>
      currentValues.filter((value) => !preferentialConditionsAllSet.has(value));
    const extractSelected = (options: OptionProps[] | BusinessTypeFilterOption[]) =>
      options
        .filter((option) => preferentialConditions.includes(option.value))
        .map((option) => option.value);
    const mergeValues = (
      currentValues: string[],
      options: OptionProps[] | BusinessTypeFilterOption[],
    ) => [...preserveNonPreferential(currentValues), ...extractSelected(options)];

    onFormValuesChange({
      ...formValues,
      "q[feature][]": mergeValues(formValues["q[feature][]"], filteredFeatureOptions),
      "q[welfare][]": mergeValues(formValues["q[welfare][]"], welfareOptions),
      "q[working_day][]": mergeValues(formValues["q[working_day][]"], workingDayOptions),
      "q[holiday][]": mergeValues(formValues["q[holiday][]"], holidayOptions),
      "q[workstyle][]": mergeValues(formValues["q[workstyle][]"], workstyleOptions),
      "q[occupation][]": mergeValues(formValues["q[occupation][]"], filteredOccupationOptions),
      "q[facility_type][]": mergeValues(
        formValues["q[facility_type][]"],
        filteredFacilityTypeOptions,
      ),
      "q[work_description][]": mergeValues(
        formValues["q[work_description][]"],
        filteredWorkDescriptionOptions,
      ),
      "q[user_credential][]": mergeValues(
        formValues["q[user_credential][]"],
        filteredUserCredentialOptions,
      ),
      "q[experience][]": mergeValues(formValues["q[experience][]"], filteredExperienceOptions),
      "q[customer_type][]": mergeValues(
        formValues["q[customer_type][]"],
        filteredCustomerTypeOptions,
      ),
      "q[salon_tour_flag]": preferentialConditions.includes("salon_tour_flag"),
      "q[client_end_up_close_flag]": preferentialConditions.includes("client_end_up_close_flag"),
    });
  };

  // 各AccordionToggleの初期開閉状態を判定する変数
  const hasSelectedOccupation = formValues["q[occupation][]"].length > 0;
  const hasSelectedFacilityType = formValues["q[facility_type][]"].length > 0;
  const hasSelectedCustomerType = formValues["q[customer_type][]"].length > 0;
  const hasSelectedFeature = formValues["q[feature][]"].length > 0;
  const hasSelectedExperience = formValues["q[experience][]"].length > 0;
  const hasSelectedWelfare = formValues["q[welfare][]"].length > 0;
  const hasSelectedWorkDescription = formValues["q[work_description][]"].length > 0;
  const hasSelectedUserCredential = formValues["q[user_credential][]"].length > 0;

  // 勤務体制・休日の統合処理を簡潔化
  const { workingConditionsOptions, selectedWorkingConditions, handleWorkingConditionsChange } =
    useMemo(() => {
      const options: OptionProps[] = [...workingDayOptions, ...holidayOptions, ...workstyleOptions];

      const selected = [
        ...formValues["q[working_day][]"],
        ...formValues["q[holiday][]"],
        ...formValues["q[workstyle][]"],
      ];

      const handleChange = (selectedValues: string[]) => {
        const workingDaySet = new Set(workingDayOptions.map((o) => o.value));
        const holidaySet = new Set(holidayOptions.map((o) => o.value));
        const workstyleSet = new Set(workstyleOptions.map((o) => o.value));

        onFormValuesChange({
          "q[working_day][]": selectedValues.filter((v) => workingDaySet.has(v)),
          "q[holiday][]": selectedValues.filter((v) => holidaySet.has(v)),
          "q[workstyle][]": selectedValues.filter((v) => workstyleSet.has(v)),
        });
      };

      return {
        workingConditionsOptions: options,
        selectedWorkingConditions: selected,
        handleWorkingConditionsChange: handleChange,
      };
    }, [workingDayOptions, holidayOptions, workstyleOptions, formValues, onFormValuesChange]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.selectorWrapper}>
          <div className={styles.searchConditionsModalWrapper}>
            <div className={styles.searchConditionsModalWrapperInner}>
              <SearchConditionsModal
                registrationStep1Url={registrationStep1Url}
                loginUrl={loginUrl}
                isLoggedIn={isLoggedIn}
                title="保存した条件"
                iconName="save"
                conditions={savedConditions.conditions}
                editPageUrl={savedConditions.editPageUrl}
                editPageLinkText="保存した検索条件を管理する"
                conditionGaLabel="saved"
                summaryGaLabel="saved_click_modal"
                emptyMessage="保存した条件はありません。"
              />
              <SearchConditionsModal
                registrationStep1Url={registrationStep1Url}
                loginUrl={loginUrl}
                isLoggedIn={isLoggedIn}
                title="検索履歴"
                iconName="time"
                conditions={searchedConditions.conditions}
                editPageUrl={searchedConditions.editPageUrl}
                editPageLinkText="検索履歴を管理する"
                conditionGaLabel="log"
                summaryGaLabel="log_click_modal"
                emptyMessage="検索履歴はありません。"
              />
            </div>
          </div>
        </div>
        <div className={styles.searchFormHeading}>
          <Heading tag="p" size="large">
            詳細な条件で検索
          </Heading>
        </div>
        <div className={styles.selectorWrapper}>
          <div className={styles.headingWrapper}>
            <Heading tag="p" size="small">
              勤務地
            </Heading>
          </div>
          <AreaSelector
            prefectureValue={formValues["q[pref]"]}
            onPrefectureChange={(prefecture) => onFormValuesChange({ "q[pref]": prefecture })}
            prefectureOptions={prefectureOptions}
            cityValues={formValues["q[city][]"]}
            onCityChange={(cities) => onFormValuesChange({ "q[city][]": cities })}
            cityChoices={cityChoices}
            lineValues={formValues["q[line][]"]}
            onLineChange={(lines) => onFormValuesChange({ "q[line][]": lines })}
            stationValues={formValues["q[station][]"]}
            onStationChange={(stations) => onFormValuesChange({ "q[station][]": stations })}
            lineChoices={lineChoices}
            popularAreaValues={formValues["q[popular_area][]"]}
            onPopularAreaChange={(popularAreas) =>
              onFormValuesChange({ "q[popular_area][]": popularAreas })
            }
            popularAreaChoices={popularAreaChoices}
            isActionButtonDisabled={isActionButtonDisabled}
            appDlUrl={appDlUrl}
            appImgSrc={appImgSrc}
            appGaLabel={appGaLabel}
            isLoggedIn={isLoggedIn}
            isSp={isSp}
          />
        </div>
        <div className={styles.selectorWrapper}>
          <div className={styles.headingWrapper}>
            <Heading tag="p" size="small">
              職種
            </Heading>
          </div>
          <BusinessTypeSelector
            values={formValues["q[business_type][]"]}
            onChange={handleBusinessTypesChange}
            businessTypeOptions={businessTypeOptions}
          />
        </div>
        <div className={styles.selectorWrapper}>
          <div className={styles.headingWrapper}>
            <Heading tag="p" size="small">
              雇用形態
            </Heading>
          </div>
          <ChoiceChip
            values={formValues["q[employment][]"]}
            choices={convertOptionsToChoices(filteredEmploymentTypeOptions)}
            onChange={(employmentTypes) =>
              onFormValuesChange({ "q[employment][]": employmentTypes })
            }
          />
        </div>
        <div className={styles.selectorWrapper}>
          <div className={styles.headingWrapper}>
            <Heading tag="p" size="small">
              給与
            </Heading>
          </div>
          <div className={styles.salaryWrapper}>
            <SelectBox
              placeholder="希望給与形態"
              selected={!!formValues["q[salary_type]"]}
              value={formValues["q[salary_type]"]}
              options={salaryTypeOptions}
              onChange={(e) =>
                onFormValuesChange({
                  "q[salary_type]": e.target.value,
                  "q[salary_min]": "",
                  "q[salary_max]": "",
                })
              }
              aria-label="希望給与形態"
            />
            {formValues["q[salary_type]"] &&
              formValues["q[salary_type]"] !== SALARY_TYPE_PERCENTAGE && (
                <div className={styles.salaryRange}>
                  <SelectBox
                    placeholder="指定なし"
                    selected={!!formValues["q[salary_min]"]}
                    value={formValues["q[salary_min]"]}
                    options={salaryMinOptions}
                    onChange={(e) => onFormValuesChange({ "q[salary_min]": e.target.value })}
                    disabled={isSalaryDataLoading}
                    aria-label="給与下限"
                  />
                  円〜
                  <SelectBox
                    placeholder="指定なし"
                    selected={!!formValues["q[salary_max]"]}
                    value={formValues["q[salary_max]"]}
                    options={salaryMaxOptions}
                    onChange={(e) => onFormValuesChange({ "q[salary_max]": e.target.value })}
                    disabled={isSalaryDataLoading}
                    aria-label="給与上限"
                  />
                  円
                </div>
              )}
          </div>
        </div>
        <div className={styles.accordionWrapper}>
          <AccordionToggle title="勤務体制・休日" defaultIsOpened>
            <ChoiceChip
              values={selectedWorkingConditions}
              choices={convertOptionsToChoices(workingConditionsOptions)}
              onChange={handleWorkingConditionsChange}
            />
          </AccordionToggle>
        </div>
        {formValues["q[business_type][]"].length > 0 && (
          <div className={styles.accordionWrapper}>
            <AccordionToggle title="こだわり条件" defaultIsOpened>
              <ChoiceChip
                values={selectedPreferentialConditions}
                choices={convertOptionsToChoices(filteredPreferentialConditionsOptions)}
                onChange={handlePreferentialConditionsChange}
              />
            </AccordionToggle>
          </div>
        )}
        <div className={styles.accordionWrapper}>
          <AccordionToggle
            title="役職・採用対象"
            ariaLabelPrefix="役職・採用対象"
            defaultIsOpened={hasSelectedOccupation}
          >
            <ChoiceChip
              values={formValues["q[occupation][]"]}
              choices={convertOptionsToChoices(filteredOccupationOptions)}
              onChange={(occupations) => onFormValuesChange({ "q[occupation][]": occupations })}
            />
          </AccordionToggle>
        </div>
        <div className={styles.accordionWrapper}>
          <AccordionToggle
            title="施設形態"
            ariaLabelPrefix="施設形態"
            defaultIsOpened={hasSelectedFacilityType}
          >
            <ChoiceChip
              values={formValues["q[facility_type][]"]}
              choices={convertOptionsToChoices(filteredFacilityTypeOptions)}
              onChange={(facilityTypes) =>
                onFormValuesChange({ "q[facility_type][]": facilityTypes })
              }
            />
          </AccordionToggle>
        </div>
        {isBiyo && (
          <div className={styles.accordionWrapper}>
            <AccordionToggle
              title="客層"
              ariaLabelPrefix="客層"
              defaultIsOpened={hasSelectedCustomerType}
            >
              <ChoiceChip
                values={formValues["q[customer_type][]"]}
                choices={convertOptionsToChoices(filteredCustomerTypeOptions)}
                onChange={(customerTypes) =>
                  onFormValuesChange({ "q[customer_type][]": customerTypes })
                }
              />
            </AccordionToggle>
          </div>
        )}
        <div className={styles.accordionWrapper}>
          <AccordionToggle title="特徴" ariaLabelPrefix="特徴" defaultIsOpened={hasSelectedFeature}>
            <ChoiceChip
              values={formValues["q[feature][]"]}
              choices={convertOptionsToChoices(filteredFeatureOptions)}
              onChange={(features) => onFormValuesChange({ "q[feature][]": features })}
            />
          </AccordionToggle>
        </div>
        {isBiyo && (
          <div className={styles.accordionWrapper}>
            <AccordionToggle
              title="必要経験"
              ariaLabelPrefix="必要経験"
              defaultIsOpened={hasSelectedExperience}
            >
              <ChoiceChip
                values={formValues["q[experience][]"]}
                choices={convertOptionsToChoices(filteredExperienceOptions)}
                onChange={(experiences) => onFormValuesChange({ "q[experience][]": experiences })}
              />
            </AccordionToggle>
          </div>
        )}
        <div className={styles.accordionWrapper}>
          <AccordionToggle
            title="福利厚生"
            ariaLabelPrefix="福利厚生"
            defaultIsOpened={hasSelectedWelfare}
          >
            <ChoiceChip
              values={formValues["q[welfare][]"]}
              choices={convertOptionsToChoices(welfareOptions)}
              onChange={(welfares) => onFormValuesChange({ "q[welfare][]": welfares })}
            />
          </AccordionToggle>
        </div>
        <div className={styles.accordionWrapper}>
          <AccordionToggle
            title="仕事内容"
            ariaLabelPrefix="仕事内容"
            defaultIsOpened={hasSelectedWorkDescription}
          >
            <ChoiceChip
              values={formValues["q[work_description][]"]}
              choices={convertOptionsToChoices(filteredWorkDescriptionOptions)}
              onChange={(workDescriptions) =>
                onFormValuesChange({ "q[work_description][]": workDescriptions })
              }
            />
          </AccordionToggle>
        </div>
        <div className={styles.accordionWrapper}>
          <AccordionToggle
            title="必要資格"
            ariaLabelPrefix="必要資格"
            defaultIsOpened={hasSelectedUserCredential}
          >
            <ChoiceChip
              values={formValues["q[user_credential][]"]}
              choices={convertOptionsToChoices(filteredUserCredentialOptions)}
              onChange={(userCredentials) =>
                onFormValuesChange({ "q[user_credential][]": userCredentials })
              }
            />
          </AccordionToggle>
        </div>
        <div className={styles.keywordsWrapper}>
          <div className={styles.headingWrapper}>
            <Heading tag="p" size="small">
              キーワード
            </Heading>
          </div>
          <div className={styles.textFieldWrapper}>
            <TextField
              key={formValues["q[keywords]"]} // NOTE: クリア時などにkeyを変更して再マウントさせ、defaultValueをリセット
              placeholder="キーワードを入力"
              defaultValue={formValues["q[keywords]"]}
              onBlur={(event) => onFormValuesChange({ "q[keywords]": event.target.value })}
            />
          </div>
        </div>
        <div className={styles.searchActionsWrapper}>
          <SearchActions
            currentWorksCount={currentWorksCount}
            isAnimating={isAnimating}
            isSaveButtonVisible={isLoggedIn}
            onSearch={onSearch}
            onSave={onSave}
          />
          <TextButton size="large" className={styles.clearButton} onClick={onClear}>
            条件をクリア
          </TextButton>
        </div>
      </div>
    </>
  );
};

const convertOptionsToChoices = (options: OptionProps[]) =>
  options.map((option) => ({
    ...option,
    id: option.value,
  }));
